import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, UserRound, CalendarCheck } from "lucide-react";
import { Page, Panel, Field, MoneyField, SelectField, HideDailyToggle } from "../lib/ui";
import { api, money, today, humanDate, normalizePhoneTR } from "../lib/api";

const EMPTY_EMP = { name: "", phone: "", position: "", net_salary: 0, start_date: today(), note: "", active: true };

const balanceText = (b) => (Math.abs(b) < 0.01 ? "Bakiye yok" : b > 0 ? `Çalışana borçluyuz: ${money(b)}` : `Çalışan borçlu: ${money(-b)}`);
const balanceTone = (b) => (Math.abs(b) < 0.01 ? "" : b > 0 ? "tone-owed" : "tone-debtor");

function EmployeeForm({ initial, onSaved, onCancel }) {
  const [form, setForm] = useState(initial || EMPTY_EMP);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try {
      const body = { ...form, net_salary: Number(form.net_salary || 0) };
      const { data } = initial?.id ? await api.put(`/employees/${initial.id}`, body) : await api.post("/employees", body);
      onSaved(data);
    } catch (err) { setError(err.response?.data?.detail || "Kaydedilemedi"); }
  };
  return (
    <form className="install-form" onSubmit={submit} data-testid="employee-form">
      <div className="form-grid three">
        <Field testid="employee-name-input" label="Ad soyad" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <Field testid="employee-position-input" label="Görevi" value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} />
        <Field testid="employee-phone-input" label="Telefon" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
               onBlur={(e) => setForm({ ...form, phone: normalizePhoneTR(e.target.value) })} />
        <MoneyField testid="employee-salary-input" label="Net maaş (₺)" value={form.net_salary} onChange={(e) => setForm({ ...form, net_salary: e.target.value })} />
        <Field testid="employee-start-input" label="İşe giriş" type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
        <SelectField testid="employee-active-select" label="Durum" value={form.active ? "1" : "0"} onChange={(e) => setForm({ ...form, active: e.target.value === "1" })}>
          <option value="1">Aktif</option><option value="0">Pasif</option>
        </SelectField>
      </div>
      {error && <div className="form-error" data-testid="employee-form-error">{error}</div>}
      <div className="button-row">
        <button type="button" className="secondary-button" onClick={onCancel}>Vazgeç</button>
        <button type="submit" className="primary-button" data-testid="employee-save">{initial?.id ? "Güncelle" : "Kaydet"}</button>
      </div>
    </form>
  );
}

function EntryForm({ emp, types, accounts, onSaved }) {
  const [form, setForm] = useState({ type: "maas_odeme", amount: emp.net_salary || 0, date: today(), account_id: "", period: today().slice(0, 7), description: "" });
  const [error, setError] = useState("");
  const t = types.find((x) => x.key === form.type);
  const submit = async (e) => {
    e.preventDefault(); setError("");
    try {
      await api.post("/payroll", { ...form, employee_id: emp.id, amount: Number(form.amount || 0), account_id: t?.cash ? form.account_id : null });
      setForm({ ...form, amount: 0, description: "" }); onSaved();
    } catch (err) { setError(err.response?.data?.detail || "Kaydedilemedi"); }
  };
  return (
    <form className="install-form" onSubmit={submit} data-testid="payroll-entry-form">
      <div className="form-grid three">
        <SelectField testid="payroll-type-select" label="İşlem" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value, ...(e.target.value === "calisma_bedeli" ? { amount: 0 } : {}) })}>
          {types.map((x) => <option key={x.key} value={x.key}>{x.label}</option>)}
        </SelectField>
        <MoneyField testid="payroll-amount-input" label="Tutar (₺)" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
        <Field testid="payroll-date-input" label="Tarih" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
        {t?.cash && (
          <SelectField testid="payroll-account-select" label={t.cash === "out" ? "Ödeme yapılan kasa" : "Tahsil edilen kasa"} value={form.account_id}
                       onChange={(e) => setForm({ ...form, account_id: e.target.value })}>
            <option value="">— Kasa / banka seçin —</option>
            {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </SelectField>
        )}
        <Field testid="payroll-period-input" label="Dönem" type="month" value={form.period} onChange={(e) => setForm({ ...form, period: e.target.value })} />
        {form.type === "calisma_bedeli" ? (
          <Field testid="payroll-work-period-input" label="Çalışma süresi (haftalık, günlük, 2 günlük…)" value={form.description} required
                 placeholder="Örn: 2 günlük · 14-15 Ekim" onChange={(e) => setForm({ ...form, description: e.target.value })} />
        ) : (
          <Field testid="payroll-desc-input" label="Açıklama" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        )}
      </div>
      <small className="hint">{form.type === "calisma_bedeli" ? "Girilen bedel çalışanın borcundan düşer · kasaya işlenmez" : <>{t?.sign > 0 ? "Çalışanın alacağını artırır" : "Çalışanın alacağını azaltır / borçlandırır"}{t?.cash ? ` · kasadan ${t.cash === "out" ? "para çıkışı" : "para girişi"} oluşturur` : " · kasaya işlenmez"}</>}</small>
      {t?.cash && <HideDailyToggle form={form} setForm={setForm} testid="payroll-hide-daily" />}
      {error && <div className="form-error" data-testid="payroll-form-error">{error}</div>}
      <div className="button-row"><button type="submit" className="primary-button" data-testid="payroll-save"><Plus size={15} /> İşlemi kaydet</button></div>
    </form>
  );
}

function EmployeeLedger({ emp, types, accounts, onChanged, onEdit, onDelete, tick }) {
  const [entries, setEntries] = useState([]);
  const load = () => api.get(`/employees/${emp.id}/entries`).then((r) => setEntries(r.data));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [emp.id, tick]);
  const sign = (k) => types.find((x) => x.key === k)?.sign || 0;
  const del = async (r) => {
    if (!window.confirm("Bu işlem silinsin mi? (Kasa hareketi de silinir)")) return;
    await api.delete(`/payroll/${r.id}`); load(); onChanged();
  };
  const by = emp.by_type || {};
  return (
    <Panel title={emp.name} subtitle={`${emp.position || "Çalışan"} · Net maaş ${money(emp.net_salary)}`}
           action={<div className="button-row">
             <button className="secondary-button" data-testid="employee-edit" onClick={onEdit}><Pencil size={14} /> Düzenle</button>
             <button className="secondary-button tone-danger" data-testid="employee-delete" onClick={onDelete}><Trash2 size={14} /> Sil</button>
           </div>}>
      <div className="payroll-summary" data-testid="employee-summary">
        <div className={`ps-card ${balanceTone(emp.balance)}`}><span>Bakiye</span><strong data-testid="employee-balance">{balanceText(emp.balance)}</strong></div>
        <div className="ps-card"><span>Ödenen maaş</span><strong>{money(by.maas_odeme || 0)}</strong></div>
        <div className="ps-card"><span>Avanslar</span><strong data-testid="employee-total-avans">{money(by.avans || 0)}</strong></div>
        <div className="ps-card"><span>Verilen borç</span><strong>{money(by.borc || 0)}</strong></div>
        <div className="ps-card"><span>Tahsilat</span><strong>{money(by.tahsilat || 0)}</strong></div>
      </div>
      <EntryForm emp={emp} types={types} accounts={accounts} onSaved={() => { load(); onChanged(); }} />
      <div className="table-wrap" data-testid="payroll-entries-table">
        <table>
          <thead><tr><th>Tarih</th><th>İşlem</th><th>Dönem</th><th>Açıklama</th><th className="amount">Alacak</th><th className="amount">Borç</th><th></th></tr></thead>
          <tbody>
            {entries.length ? entries.map((r) => (
              <tr key={r.id} data-testid={`payroll-row-${r.id}`}>
                <td>{humanDate(r.date)}</td>
                <td><strong>{r.type_label}</strong>{r.account_id && <small>{accounts.find((a) => a.id === r.account_id)?.name || "Kasa"}</small>}</td>
                <td>{r.period || "—"}</td>
                <td>{r.description || "—"}</td>
                <td className="amount positive">{sign(r.type) > 0 ? money(r.amount) : ""}</td>
                <td className="amount negative">{sign(r.type) < 0 ? money(r.amount) : ""}</td>
                <td><button className="row-icon tone-danger" data-testid={`payroll-delete-${r.id}`} onClick={() => del(r)}><Trash2 size={14} /></button></td>
              </tr>
            )) : <tr><td colSpan={7} className="empty">Henüz işlem yok.</td></tr>}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}

export default function Payroll({ accounts = [], onAccountsChanged }) {
  const [tick, setTick] = useState(0);
  const [emps, setEmps] = useState([]);
  const [types, setTypes] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [editing, setEditing] = useState(null);
  const [msg, setMsg] = useState("");
  const load = () => api.get("/employees").then((r) => { setEmps(r.data); setActiveId((id) => id || r.data[0]?.id || null); });
  const changed = () => { load(); onAccountsChanged?.(); };
  useEffect(() => { load(); api.get("/payroll/types").then((r) => setTypes(r.data)); }, []);

  const accrue = async () => {
    const period = window.prompt("Maaş tahakkuku yapılacak dönem (YYYY-AA):", today().slice(0, 7));
    if (!period) return;
    try {
      const { data } = await api.post("/payroll/accrue-month", { period });
      setMsg(`${period}: ${data.created} çalışana maaş tahakkuku işlendi`); load(); setTick((t) => t + 1);
    } catch (e) { setMsg(e.response?.data?.detail || "Tahakkuk yapılamadı"); }
  };
  const remove = async (emp) => {
    if (!window.confirm(`${emp.name} silinsin mi?`)) return;
    try { await api.delete(`/employees/${emp.id}`); setActiveId(null); load(); } catch (e) { setMsg(e.response?.data?.detail || "Silinemedi"); }
  };
  const active = emps.find((e) => e.id === activeId);
  const totalOwed = emps.reduce((s, e) => s + (e.balance > 0 ? e.balance : 0), 0);
  const totalDebt = emps.reduce((s, e) => s + (e.balance < 0 ? -e.balance : 0), 0);

  return (
    <>
      <Page eyebrow="PERSONEL" title="Maaş & Personel"
            subtitle={`Çalışanlara borcumuz ${money(totalOwed)} · Çalışanların borcu ${money(totalDebt)}`}
            action={<div className="button-row">
              <button className="secondary-button" data-testid="payroll-accrue" onClick={accrue}><CalendarCheck size={15} /> Aylık maaş tahakkuku</button>
              <button className="primary-button" data-testid="employee-new" onClick={() => setEditing({})}><Plus size={15} /> Yeni çalışan</button>
            </div>} />
      {msg && <div className="sync-msg" data-testid="payroll-msg">{msg}</div>}
      {editing && <EmployeeForm initial={editing.id ? editing : null} onCancel={() => setEditing(null)}
                                onSaved={(d) => { setEditing(null); setActiveId(d.id); load(); }} />}
      <div className="pl-layout">
        <aside className="pl-sidebar" data-testid="employee-list">
          {emps.map((e) => (
            <button key={e.id} type="button" data-testid={`employee-item-${e.id}`} className={`pl-item ${e.id === activeId ? "active" : ""} ${e.active ? "" : "inactive"}`}
                    onClick={() => setActiveId(e.id)}>
              <UserRound size={14} /><span>{e.name}</span>
              <small className={balanceTone(e.balance)}>{balanceText(e.balance)}</small>
            </button>
          ))}
          {!emps.length && <p className="empty">Henüz çalışan yok.</p>}
        </aside>
        {active ? <EmployeeLedger key={active.id} emp={active} types={types} accounts={accounts} onChanged={changed} tick={tick}
                                  onEdit={() => setEditing(active)} onDelete={() => remove(active)} />
          : <Panel title="Çalışan seçin"><p className="empty">Soldan çalışan seçin veya yeni çalışan ekleyin.</p></Panel>}
      </div>
    </>
  );
}
